import { DateTime } from "luxon";
import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const WEEK = [1, 2, 3, 4, 5, 6, 0];
const DEFAULTS = [
  ["09:00", "12:00"],
  ["14:00", "19:00"],
];

const SELECT_FIELD_CLASS =
  "appearance-none bg-white border border-[#d3d3d3] rounded px-3 h-10 text-[14px] text-[#222] cursor-pointer";

const TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 0; h < 24; h += 1) {
    for (const m of [0, 30]) {
      out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return out;
})();

const SelectChevron = () => (
  <svg
    className="h-4 w-4 text-[#222]"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

function StyledSelect({ value, onChange, children, className = "", ...rest }) {
  return (
    <div className={`relative ${className}`}>
      <select
        className={`${SELECT_FIELD_CLASS} w-full pr-10`}
        value={value}
        onChange={onChange}
        {...rest}
      >
        {children}
      </select>
      <div
        className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
        aria-hidden
      >
        <SelectChevron />
      </div>
    </div>
  );
}

const looksLikeMorningAfternoon = (v) => {
  const d = v?.[1] || v?.[2] || v?.[3] || v?.[4] || v?.[5] || v?.[6] || v?.[0];
  return !!(
    d &&
    (Array.isArray(d.morning) ||
      Array.isArray(d.afternoon) ||
      d.morning?.start ||
      d.afternoon?.start)
  );
};

const looksLikeLegacyRanges = (v) => {
  const d = v?.[1] || v?.[2] || v?.[3] || v?.[4] || v?.[5] || v?.[6] || v?.[0];
  return !!(d && Array.isArray(d.ranges));
};

function ensureSlots(value) {
  const out = {};
  for (const d of WEEK) {
    let slot1 = ["", ""],
      slot2 = ["", ""];

    if (looksLikeLegacyRanges(value)) {
      const ranges = Array.isArray(value?.[d]?.ranges) ? value[d].ranges : [];
      const r1 = ranges[0],
        r2 = ranges[1];
      if (r1) slot1 = [r1?.start || "", r1?.end || ""];
      if (r2) slot2 = [r2?.start || "", r2?.end || ""];
    } else if (looksLikeMorningAfternoon(value)) {
      const p = value?.[d] || {};
      if (Array.isArray(p.morning))
        slot1 = [p.morning[0] || "", p.morning[1] || ""];
      else if (p?.morning) slot1 = [p.morning.start || "", p.morning.end || ""];
      if (Array.isArray(p.afternoon))
        slot2 = [p.afternoon[0] || "", p.afternoon[1] || ""];
      else if (p?.afternoon)
        slot2 = [p.afternoon.start || "", p.afternoon.end || ""];
    } else if (value?.[d]?.slots) {
      const s = value[d].slots;
      slot1 = Array.isArray(s?.[0]) ? [s[0][0] || "", s[0][1] || ""] : ["", ""];
      slot2 = Array.isArray(s?.[1]) ? [s[1][0] || "", s[1][1] || ""] : ["", ""];
    }

    out[d] = { slots: [slot1, slot2] };
  }
  return out;
}

function slotsToLegacy(slotsObj) {
  const out = {};
  for (const d of WEEK) {
    const [s1, s2] = slotsObj?.[d]?.slots || [[], []];
    const ranges = [];
    if (s1?.length === 2 && s1[0] && s1[1])
      ranges.push({ start: s1[0], end: s1[1] });
    if (s2?.length === 2 && s2[0] && s2[1])
      ranges.push({ start: s2[0], end: s2[1] });
    out[d] = { ranges };
  }
  return out;
}

const formatTzLabel = (tz) => {
  const dt = DateTime.now().setZone(tz),
    off = dt.offset,
    sign = off >= 0 ? "+" : "-";
  const abs = Math.abs(off),
    hh = String(Math.floor(abs / 60)).padStart(2, "0"),
    mm = String(abs % 60).padStart(2, "0");
  return `${tz} (UTC${sign}${hh}:${mm})`;
};

function localToUtc(openingLocal, tz) {
  const out = {},
    refMonday = DateTime.now().setZone(tz).startOf("week").plus({ days: 1 });
  for (const day of WEEK) {
    const conf = openingLocal?.[day];
    out[day] = [];
    if (!conf || !Array.isArray(conf.ranges) || conf.ranges.length === 0)
      continue;
    const offsetDays = day === 0 ? 6 : day - 1; // Sun -> after Sat
    const refDay = refMonday.plus({ days: offsetDays });
    for (const r of conf.ranges) {
      if (!r?.start || !r?.end) continue;
      const [sh, sm] = String(r.start).split(":").map(Number);
      const [eh, em] = String(r.end).split(":").map(Number);
      const startUtc = refDay
        .set({ hour: sh || 0, minute: sm || 0 })
        .setZone(tz, { keepLocalTime: true })
        .toUTC();
      const endUtc = refDay
        .set({ hour: eh || 0, minute: em || 0 })
        .setZone(tz, { keepLocalTime: true })
        .toUTC();
      out[day].push([startUtc.toFormat("HH:mm"), endUtc.toFormat("HH:mm")]);
    }
  }
  return out;
}

export function utcToLocalOpening(openingUtc, timezone = "Europe/Paris") {
  const result = {};
  const refMondayLocal = DateTime.now()
    .setZone(timezone)
    .startOf("week")
    .plus({ days: 1 });
  for (const day of WEEK) {
    result[day] = { ranges: [] };
    const pairs = Array.isArray(openingUtc?.[day]) ? openingUtc[day] : [];
    if (pairs.length === 0) continue;
    const offsetDays = day === 0 ? 6 : day - 1;
    const refDayLocal = refMondayLocal.plus({ days: offsetDays });
    for (const [startUtc, endUtc] of pairs) {
      if (!startUtc || !endUtc) continue;
      const [sh, sm] = String(startUtc).split(":").map(Number);
      const [eh, em] = String(endUtc).split(":").map(Number);
      const start = DateTime.utc(
        refDayLocal.year,
        refDayLocal.month,
        refDayLocal.day,
        sh || 0,
        sm || 0,
      ).setZone(timezone);
      const end = DateTime.utc(
        refDayLocal.year,
        refDayLocal.month,
        refDayLocal.day,
        eh || 0,
        em || 0,
      ).setZone(timezone);
      result[day].ranges.push({
        start: start.toFormat("HH:mm"),
        end: end.toFormat("HH:mm"),
      });
    }
  }
  return result;
}

export function computeUtcOpening(value, timezone) {
  const legacy = slotsToLegacy(ensureSlots(value || {}));
  return localToUtc(legacy, timezone);
}

const IconButton = ({ children, onClick, disabled, title, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#d3d3d3] bg-white text-[#222] hover:bg-[#f5f5f5] disabled:cursor-not-allowed disabled:opacity-40 ${className}`}
  >
    {children}
  </button>
);

const IconBan = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path
      d="M7 7l10 10"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const IconCopy = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect
      x="8"
      y="8"
      width="11"
      height="11"
      rx="1.5"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M6 16V6a2 2 0 012-2h10"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

export default function OpeningHoursEditor({
  value,
  timezone = "Europe/Paris",
  onChange,
  variant = "default",
}) {
  const { t } = useTranslation();

  const DAYS = useMemo(
    () => [
      { key: 1, short: t("Monday_short"), label: t("Monday") },
      { key: 2, short: t("Tuesday_short"), label: t("Tuesday") },
      { key: 3, short: t("Wednesday_short"), label: t("Wednesday") },
      { key: 4, short: t("Thursday_short"), label: t("Thursday") },
      { key: 5, short: t("Friday_short"), label: t("Friday") },
      { key: 6, short: t("Saturday_short"), label: t("Saturday") },
      { key: 0, short: t("Sunday_short"), label: t("Sunday") },
    ],
    [t],
  );

  const siteSettingsDays = useMemo(
    () => [
      { key: 1, label: `${t("Monday_abbr")}.`, title: t("Monday") },
      { key: 2, label: `${t("Tuesday_abbr")}.`, title: t("Tuesday") },
      { key: 3, label: `${t("Wednesday_abbr")}.`, title: t("Wednesday") },
      { key: 4, label: `${t("Thursday_abbr")}.`, title: t("Thursday") },
      { key: 5, label: `${t("Friday_abbr")}.`, title: t("Friday") },
      { key: 6, label: `${t("Saturday_abbr")}.`, title: t("Saturday") },
      { key: 0, label: `${t("Sunday_abbr")}.`, title: t("Sunday") },
    ],
    [t],
  );

  const TZ_OPTIONS = [
    "Europe/Paris",
    "UTC",
    "Europe/Brussels",
    "Europe/Madrid",
    "Europe/Berlin",
    "Europe/London",
    "Europe/Stockholm",
  ];

  const slots = useMemo(() => ensureSlots(value || {}), [value]);

  const emit = (nextSlots, tz = timezone) =>
    onChange?.({
      openingLocalParts: nextSlots, // front compact
      openingLocal: slotsToLegacy(nextSlots), // legacy compatibility
      timezone: tz,
    });

  const addSlot = (day) => {
    const [s1, s2] = slots[day].slots;
    const isFilled = (s) => Array.isArray(s) && s[0] && s[1];
    let nextS1 = s1,
      nextS2 = s2;
    if (!isFilled(s1)) nextS1 = [...DEFAULTS[0]];
    else if (!isFilled(s2)) nextS2 = [...DEFAULTS[1]];
    else return; // already 2
    emit({ ...slots, [day]: { slots: [nextS1, nextS2] } });
  };

  const removeSlot = (day, idx) => {
    const next = [...slots[day].slots];
    next[idx] = ["", ""]; // keep position empty for order
    emit({ ...slots, [day]: { slots: next } });
  };

  const setTime = (day, idx, pos, val) => {
    const i = typeof pos === "string" ? (pos === "start" ? 0 : 1) : pos;
    const daySlots = [...slots[day].slots];
    const cur =
      Array.isArray(daySlots[idx]) && daySlots[idx].length === 2
        ? [...daySlots[idx]]
        : ["", ""];
    cur[i] = val || "";
    daySlots[idx] = cur;
    emit({ ...slots, [day]: { slots: daySlots } });
  };

  const setTz = (e) => emit(slots, e.target.value);

  const clearDay = (day) => {
    emit({
      ...slots,
      [day]: {
        slots: [
          ["", ""],
          ["", ""],
        ],
      },
    });
  };

  const duplicateSlot = (day) => {
    const [s1, s2] = slots[day].slots;
    if (!isFilledSlot(s1) || isFilledSlot(s2)) return;
    emit({ ...slots, [day]: { slots: [s1, [...s1]] } });
  };

  const isFilledSlot = (s) => Array.isArray(s) && s[0] && s[1];

  if (variant === "site-settings") {
    return (
      <div className="space-y-6 px-8 pb-8">
        <div>
          <label className="block mb-1 text-[14px] leading-[22px] font-semibold text-[#222]">
            {t("Time zone")}
          </label>
          <StyledSelect value={timezone} onChange={setTz}>
            {TZ_OPTIONS.map((tz) => (
              <option key={tz} value={tz}>
                {formatTzLabel(tz)}
              </option>
            ))}
          </StyledSelect>
        </div>

        <div className="flex flex-col gap-5">
          {siteSettingsDays.map(({ key, label, title }) => {
            const [s1, s2] = slots[key].slots;
            const row0 = Array.isArray(s1) && (s1[0] || s1[1]);
            const row1 = Array.isArray(s2) && (s2[0] || s2[1]);
            const isOpen = row0 || row1;
            const bothSlotsFull = isFilledSlot(s1) && isFilledSlot(s2);

            const timeRow = (idx) => {
              const val = slots[key].slots[idx] || ["", ""];
              if (!(val[0] || val[1])) return null;
              return (
                <div className="flex h-10 items-center gap-2">
                  <StyledSelect
                    className="w-1/2"
                    value={val[0] || "09:00"}
                    onChange={(e) => setTime(key, idx, 0, e.target.value)}
                  >
                    {TIME_OPTIONS.map((time) => (
                      <option key={`${key}-${idx}-s-${time}`} value={time}>
                        {time}
                      </option>
                    ))}
                  </StyledSelect>
                  <span className="select-none text-[14px] text-[#222]">-</span>
                  <StyledSelect
                    className="w-1/2"
                    value={val[1] || "12:00"}
                    onChange={(e) => setTime(key, idx, 1, e.target.value)}
                  >
                    {TIME_OPTIONS.map((time) => (
                      <option key={`${key}-${idx}-e-${time}`} value={time}>
                        {time}
                      </option>
                    ))}
                  </StyledSelect>
                </div>
              );
            };

            return (
              <div key={key} className="flex items-start gap-3">
                <span
                  className="w-[44px] shrink-0 pt-2 text-[14px] font-semibold text-[#222]"
                  title={title}
                >
                  {label}
                </span>
                {isOpen ? (
                  <>
                    <div className="flex min-w-0 flex-1 flex-col gap-2">
                      {timeRow(0)}
                      {timeRow(1)}
                    </div>
                    <div className="flex shrink-0 flex-col gap-2">
                      {row0 && (
                        <div className="flex h-10 items-center gap-1.5">
                          <IconButton
                            title={t("Closed")}
                            onClick={() => clearDay(key)}
                          >
                            <IconBan />
                          </IconButton>
                          <IconButton
                            title={
                              bothSlotsFull
                                ? t("Maximum 2 time ranges")
                                : t("Add time range")
                            }
                            disabled={bothSlotsFull}
                            onClick={() => addSlot(key)}
                          >
                            <span className="text-lg leading-none">+</span>
                          </IconButton>
                          <IconButton
                            title={t("Copy")}
                            onClick={() => duplicateSlot(key)}
                            disabled={!isFilledSlot(s1) || isFilledSlot(s2)}
                          >
                            <IconCopy />
                          </IconButton>
                        </div>
                      )}
                      {row1 && (
                        <div className="flex h-10 items-center">
                          <IconButton
                            title={t("Remove")}
                            onClick={() => removeSlot(key, 1)}
                          >
                            <span className="text-lg leading-none">×</span>
                          </IconButton>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <p className="flex-1 pt-2 text-[14px] italic text-[#888]">
                      *{t("settingsSites.unavailable")}*
                    </p>
                    <IconButton
                      title={t("Add time range")}
                      onClick={() => addSlot(key)}
                      className="mt-0.5"
                    >
                      <span className="text-lg leading-none">+</span>
                    </IconButton>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const Slot = ({ day, idx }) => {
    const val = slots[day].slots[idx] || ["", ""];
    const on = !!(val[0] || val[1]);
    const [draft, setDraft] = useState({
      start: val[0] || "",
      end: val[1] || "",
    });
    const [editing, setEditing] = useState({ start: false, end: false });

    useEffect(() => {
      if (!editing.start) setDraft((d) => ({ ...d, start: val[0] || "" }));
      if (!editing.end) setDraft((d) => ({ ...d, end: val[1] || "" }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [val[0], val[1]]);

    if (!on) return null;

    const commit = (pos) => {
      const v = draft[pos];
      if (/^\d{2}:\d{2}$/.test(v)) setTime(day, idx, pos, v);
      setEditing((e) => ({ ...e, [pos]: false }));
    };

    return (
      <div className="flex items-center gap-2 bg-gray-50 rounded-md px-2 py-1">
        <input
          type="time"
          step="60"
          inputMode="numeric"
          className="border rounded-md px-2 h-9 text-sm w-[110px]"
          value={draft.start}
          onFocus={() => setEditing((e) => ({ ...e, start: true }))}
          onChange={(e) => setDraft((d) => ({ ...d, start: e.target.value }))}
          onBlur={() => commit("start")}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit("start");
            }
            if (e.key === "Escape") {
              setDraft((d) => ({ ...d, start: val[0] || "" }));
              setEditing((e) => ({ ...e, start: false }));
            }
          }}
          aria-label={`day-${day}-slot-${idx}-start`}
        />
        <div className="text-gray-400 text-sm select-none">-</div>
        <input
          type="time"
          step="60"
          inputMode="numeric"
          className="border rounded-md px-2 h-9 text-sm w-[110px]"
          value={draft.end}
          onFocus={() => setEditing((e) => ({ ...e, end: true }))}
          onChange={(e) => setDraft((d) => ({ ...d, end: e.target.value }))}
          onBlur={() => commit("end")}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit("end");
            }
            if (e.key === "Escape") {
              setDraft((d) => ({ ...d, end: val[1] || "" }));
              setEditing((e) => ({ ...e, end: false }));
            }
          }}
          aria-label={`day-${day}-slot-${idx}-end`}
        />
        <button
          type="button"
          onClick={() => removeSlot(day, idx)}
          className="text-gray-500 hover:text-rose-600 px-1"
          title={t("Remove")}
          aria-label={t("Remove")}
        >
          ×
        </button>
      </div>
    );
  };

  return (
    <div className="p-5 space-y-6">
      {/* Time zone */}
      <div className="flex items-center gap-4">
        <label className="text-sm font-semibold min-w-[160px]">
          {t("Time zone")}
        </label>
        <select
          className="border rounded-md px-3 py-2 text-sm min-w-[340px]"
          value={timezone}
          onChange={setTz}
        >
          {TZ_OPTIONS.map((tz) => (
            <option key={tz} value={tz}>
              {formatTzLabel(tz)}
            </option>
          ))}
        </select>
      </div>

      {/* Days */}
      <div className="flex flex-col gap-2">
        {DAYS.map(({ key, short, label }) => {
          const [s1, s2] = slots[key].slots;
          const isFilled = (s) => Array.isArray(s) && s[0] && s[1];
          const any = isFilled(s1) || isFilled(s2);
          const canAdd = !(isFilled(s1) && isFilled(s2)); // max 2 time slots

          return (
            <div
              key={key}
              className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 min-h-[62px]"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="w-7 h-7 rounded-full bg-indigo-600 text-white text-sm font-semibold grid place-items-center"
                  title={label}
                >
                  {short}
                </div>
                {any ? (
                  <div className="flex items-center gap-3 flex-wrap">
                    <Slot day={key} idx={0} />
                    <Slot day={key} idx={1} />
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">{t("Closed")}</div>
                )}
              </div>
              <button
                type="button"
                onClick={() => canAdd && addSlot(key)}
                className="text-indigo-600 hover:text-indigo-700 text-lg px-2
                   disabled:text-gray-300 disabled:hover:text-gray-300 disabled:cursor-not-allowed"
                title={
                  canAdd ? t("Add time range") : t("Maximum 2 time ranges")
                }
                aria-label={t("Add time range")}
                disabled={!canAdd}
              >
                +
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
